/**
 * Express.js Integration
 * Type-safe Express middleware for x402 payments with Coinbase compatibility
 */

import type { Request, Response, NextFunction } from 'express'
import { X402Middleware } from '../middleware'
import { X402Client } from '../../client/fetch'
import { MiddlewareOptions, FacilitatorConfig } from '../../lib'

export interface X402ExpressMiddleware {
  (options: MiddlewareOptions): (req: Request, res: Response, next: NextFunction) => void | Promise<void>
}


export function createExpressMiddleware(client: X402Client): X402ExpressMiddleware {
  const middleware = new X402Middleware(client)

  return (options: MiddlewareOptions) => {
    return middleware.createMiddleware(options)
  }
}


/**
 * Convenience function for Express users (legacy single-route support)
 */
export function withX402(clientOrConfig: X402Client | FacilitatorConfig, options?: MiddlewareOptions): X402ExpressMiddleware | ((req: Request, res: Response, next: NextFunction) => Promise<void>) {
  // If first arg is X402Client instance
  if (clientOrConfig instanceof X402Client) {
    const middleware = new X402Middleware(clientOrConfig)
    if (options) {
      return middleware.createMiddleware(options)
    }
    return createExpressMiddleware(clientOrConfig)
  }

  // If first arg is config object, create client
  const client = new X402Client(clientOrConfig)
  const middleware = new X402Middleware(client)
  
  if (options) {
    // If options provided, return configured middleware
    return middleware.createMiddleware(options)
  }

  // Return middleware creator function
  return createExpressMiddleware(client)
}


// Type augmentation for Express Request
declare global {
  namespace Express {
    interface Request {
      x402Settlement?: {
        txHash: string
        networkId: string
        payer?: string
        gasUsed: any
      }
    }
  }
}
